import { css, html, nothing, TemplateResult } from 'lit';
import { property, query, queryAll } from 'lit/decorators.js';
import { BaseComponent } from '../../base-component/base-component';
import { SearchV3Result } from '../search-v3.interface';
import { smallSpinner } from '../../../common/constants/api-constants';
import { waitForElementsToLoad } from '../../../common/utils/delay-util';
import { BUSINESS_UNIT_LIST } from '../constants';
import '../../search-input/search-input';
import { SearchComponent } from '../../search-input/search-input';
import { checkValidISBN } from '../../search/thunks/search.thunk';

declare let CustomSelect;
const customSelectSelector = '.customSelect';

type emptyFunction = (value?: string) => void;

/**
 * Base Search V3 UI Component
 *
 * Provides the common UI structure and functionality for all search-v3 components:
 * - Business unit dropdown
 * - Search input with typeahead
 * - Search button with loading state
 * - Responsive design (small/large screen templates)
 *
 * This class should be extended by specific implementation components:
 * - SearchV3UIRedirect: For navigation-based search (default behavior)
 * - SearchV3UISupport: For button-based suggestions (Support tab)
 * - SearchV3UISearchPage: For in-place search results (search page behavior)
 */
export abstract class SearchV3UIBase extends BaseComponent {
  @queryAll(customSelectSelector) customSelectEl!: HTMLDivElement[];
  @query('mhecomm-search-input') searchInputEl!: SearchComponent;

  static styles = [
    css`
      input ~ .btn-icon {
        border-bottom-left-radius: 0;
        border-top-left-radius: 0;
        padding: 0 0.625rem;
        margin-left: -0.125rem;
      }
      input ~ .btn-icon uxt-icon svg {
        width: 1rem !important;
        height: 1rem !important;
      }
      .customSelectContainer {
        display: flex;
      }
      .customSelectContainer .customSelect > .form-select {
        border-top-right-radius: 0 !important;
        border-bottom-right-radius: 0 !important;
        background-color: var(--theme-gray-100);
        background-image: url('/uxt/static/external/icons/caret-down-fill.svg');
        background-position: right 0.75rem center;
      }
      .form-select:focus,
      mhecomm-search-input:focus {
        position: relative;
        z-index: 10;
      }
    `,
  ];

  @property()
  id = '';

  @property()
  searchPlaceholder = 'Search...';

  @property()
  searchInput = '';

  @property()
  dropdownValue = BUSINESS_UNIT_LIST[0]?.value;

  @property({ type: Boolean })
  isLoading = false;

  @property({ type: Boolean })
  renderSmallUI = false;

  @property({ type: Boolean })
  showBusinessUnitDropdown = true;

  @property({ attribute: false })
  onSearchInputChange: (value: string) => Promise<any[]> = async () => Promise.resolve([]);

  @property({ attribute: false })
  updateSearchInput: (value: string) => void = () => {
    // Default empty implementation
  };

  @property({ attribute: false })
  onDropdownChange: emptyFunction | null = null;

  @property({ attribute: false })
  onDropdownOpen: emptyFunction | null = null;

  @property({ attribute: false })
  onSearchTrigger: emptyFunction | null = null;

  @property({ attribute: false })
  searchResultClickAnalytic: ((value: SearchV3Result) => void) | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    waitForElementsToLoad(this.shadowRoot!, [customSelectSelector]).then(() => {
      this.initCustomSelect();
    });
  }

  protected updated(_changedProperties: Map<string | number | symbol, unknown>): void {
    // Both branches are independent: `renderSmallUI` toggles between the small/large
    // templates (each re-mounts the customSelect element and needs re-init), and
    // `dropdownValue` must always be pushed to the CustomSelect widget when it changes,
    // even if `renderSmallUI` also changed in the same update batch. Using `else if`
    // caused the initial dropdown value to be dropped on the first render.
    if (_changedProperties.has('renderSmallUI')) {
      this.initCustomSelect();
    }
    if (_changedProperties.has('dropdownValue')) {
      this.syncCustomSelectValue();
    }
  }

  private initCustomSelect() {
    this.customSelectEl.forEach((el) => CustomSelect.getOrCreateInstance(el));
    // Ensure the widget reflects the current `dropdownValue` on init. Without this,
    // CustomSelect falls back to its first `<li>` option (i.e. "All"/`products`) when
    // the initial `setValue` in `updated()` runs before the CustomSelect script is
    // loaded — leaving the topbar dropdown stuck on "All" on `/highered/*` and
    // `/prek-12/*` pages even though `businessUnit` is correctly set to `'he'`/`'seg'`.
    this.syncCustomSelectValue();
  }

  private syncCustomSelectValue() {
    if (typeof CustomSelect === 'undefined' || !this.dropdownValue) {
      return;
    }
    this.customSelectEl.forEach((el) =>
      CustomSelect.getOrCreateInstance(el)?.setValue(this.dropdownValue, { dispatch: false })
    );
  }

  private handleSearchSubmit(e: Event) {
    e.preventDefault();

    if (this.searchInput) {
      document.title = `Search results for "${this.searchInput}" | McGraw Hill`;
    }

    this.onSearchTrigger?.();
  }

  public clearResult() {
    this.searchInput = '';
    this.dropdownValue = '';
  }

  renderComp() {
    return html`
      <div>${this.renderSmallUI ? this.renderSmallerScreenTemplate() : this.renderLargerScreenTemplate()}</div>
    `;
  }

  private renderLargerScreenTemplate() {
    return html`
      <form class="col" @submit=${this.handleSearchSubmit.bind(this)} role="search">
        <div class="input-group position-relative">
          ${this.showBusinessUnitDropdown
            ? html`<div class="customSelectContainer">${this.bussinessUnitDrodown()}</div>`
            : nothing}
          ${this.inputTypeahead()} ${this.searchButton()}
        </div>
      </form>
    `;
  }

  private renderSmallerScreenTemplate() {
    return html`
      ${this.showBusinessUnitDropdown ? html`<div class="mb-3">${this.bussinessUnitDrodown()}</div>` : nothing}
      <form class="col" @submit=${this.handleSearchSubmit.bind(this)} role="search">
        <div class="input-group position-relative">${this.inputTypeahead()} ${this.searchButton()}</div>
      </form>
    `;
  }

  private bussinessUnitDrodown() {
    return html`<div
      id="searchLearningLevelDropdown"
      class="searchLearningLevelDropdown dropdown customSelect w-100"
      @customSelectChange=${this.onDropdownValueChange.bind(this)}
      @customSelectOnOpen=${this.onDropdownOpen?.bind(this)}>
      <div
        aria-controls="customSelectListBox"
        aria-expanded="false"
        aria-haspopup="listbox"
        aria-label="Business unit"
        data-layer-comp="mhecomm-search-v3"
        class="form-select h-100 ${this.renderSmallUI ? '' : 'search-border '}mh-emit"
        role="combobox"
        tabindex="0"
        data-custom-select="true">
        <span class="text-muted">Choose an option</span>
      </div>
      <input
        type="hidden"
        name="customSelectInput"
        class="customSelectInput"
        value="${this.dropdownValue}"
        data-automation-id="offcanvas-search-bu-dd" />
      <ul
        class="dropdown-menu p-0 mt-1 w-100 shadow-none"
        role="listbox"
        id="customSelectListBox"
        aria-label="Choose an option"
        tabindex="-1">
        ${BUSINESS_UNIT_LIST.map(
          ({ label, value }) =>
            html`<li class="customSelectOption" data-value="${value}">
              <span class="align-items-center d-flex justify-content-between text-wrap">${label}</span>
            </li>`
        )}
      </ul>
    </div>`;
  }

  protected inputTypeahead() {
    return html`<mhecomm-search-input
      .value=${this.searchInput}
      minSearchChars="3"
      inputElClassList="${this.renderSmallUI ? 'border rounded-start border-gray600' : ''}"
      class="form-control position-relative ${this.renderSmallUI ? 'border-0' : 'search-border'}"
      .updateSearchInput=${this.updateSearchInput?.bind(this)}
      .fetchFn=${this.onSearchInputChange.bind(this)}
      .getLabel=${(suggestion: any) => suggestion.term}
      .renderItem=${this.renderSuggestionItem.bind(this)}
      placeholder=${this.searchPlaceholder}
      ?hideSuggestion=${this.searchInput?.length <= 2 || checkValidISBN(this.searchInput)}></mhecomm-search-input>`;
  }

  /**
   * Renders suggestion items - must be implemented by subclasses
   * This method defines how search suggestions are displayed and behave
   *
   * @param suggestion The search suggestion data
   * @param highlighted Whether this item is currently highlighted
   * @param index The index of the item in the suggestion list
   * @param handleSelect Function to handle item selection
   */
  protected abstract renderSuggestionItem(
    suggestion: any,
    highlighted: boolean,
    index: number,
    handleSelect: (item: any) => void
  ): TemplateResult;

  private searchButton() {
    return html`<button
      type="submit"
      class="btn btn-primary search-btn btn-icon"
      aria-label=${this.isLoading ? 'Searching...' : 'Search'}
      data-automation-id="offcanvas-search-btn"
      aria-disabled=${this.isLoading ? 'true' : 'false'}
      ?disabled=${this.isLoading}>
      ${this.isLoading
        ? html` <span class="spinner-container" aria-hidden="false" aria-live="polite"> ${smallSpinner()} </span> `
        : html`
            ${this.uxtIconReady ? html`<uxt-icon icon="search" aria-hidden="true"></uxt-icon>` : nothing}
            <span class="visually-hidden">Search</span>
          `}
    </button>`;
  }

  private onDropdownValueChange(e: CustomEvent) {
    const value = (e as CustomEvent).detail?.value;
    this.dropdownValue = value;
    this.onDropdownChange?.(value);
    this.searchInputEl?.refreshResults();
  }
}
